"use client";

import { useEffect, useRef, useState } from "react";
import { Star } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ClientTestimonials() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.3
  });
  const testimonials = [{
    id: 1,
    name: "Michael Anderson",
    role: "CEO, Tech Innovations",
    text: "Phoenix Enterprise TCP provided exceptional chauffeur service for our executive team. The professionalism, punctuality, and luxury vehicles exceeded all expectations. Highly recommended for VIP transportation needs.",
    rating: 5
  }];
  return <section id="client-testimonials" ref={sectionRef} className="dark bg-background text-foreground section-padding relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-primary-dark/5 via-transparent to-primary-dark/5 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto space-y-12">
          <div className="text-center space-y-4 animate-reveal" data-index={0}>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight">
              Client Testimonials
            </h2>
            <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto">Discover why professionals trust Phoenix Enterprises Limousine Service for luxury chauffeur service</p>
          </div>

          <div className="space-y-8">
            {testimonials.map((testimonial, i) => <TestimonialCard key={testimonial.id} testimonial={testimonial} index={i} isVisible={isVisible} data-index={i} />)}
          </div>
        </div>
      </div>
    </section>;
}
function TestimonialCard({
  testimonial,
  index,
  isVisible
}: {
  testimonial: {
    id: number;
    name: string;
    role: string;
    text: string;
    rating: number;
  };
  index: number;
  isVisible: boolean;
}) {
  const [animatedRating, setAnimatedRating] = useState(0);
  const hasAnimated = useRef(false);
  useEffect(() => {
    if (isVisible && !hasAnimated.current) {
      hasAnimated.current = true;
      let current = 0;
      const interval = setInterval(() => {
        current += 1;
        setAnimatedRating(current);
        if (current >= testimonial.rating) {
          clearInterval(interval);
        }
      }, 120);
      return () => clearInterval(interval);
    }
  }, [isVisible, testimonial.rating]);
  return <Card className="glass-effect p-8 md:p-12 lg:p-16 border-white/10 hover-lift" style={{
    animationDelay: `${index * 100}ms`
  }}>
      <CardContent className="space-y-6">
        <div className="flex gap-1.5" aria-label={`Rating: ${testimonial.rating} out of 5 stars`}>
          {[1, 2, 3, 4, 5].map(star => <Star key={star} className={`size-7 md:size-8 transition-all duration-300 ${star <= animatedRating ? "fill-secondary text-secondary scale-110" : "text-white/20"}`} style={{
          transitionDelay: `${(star - 1) * 120}ms`
        }} aria-hidden="true" />)}
        </div>

        <blockquote className="text-xl md:text-2xl lg:text-3xl leading-relaxed font-light text-foreground">
          "{testimonial.text}"
        </blockquote>

        <div className="pt-4 border-t border-white/10">
          <p className="font-semibold text-lg md:text-xl text-foreground">
            {testimonial.name}
          </p>
          <p className="text-muted-foreground text-sm md:text-base mt-1">
            {testimonial.role}
          </p>
        </div>
      </CardContent>
    </Card>;
}